import React, { useContext, useEffect, useState } from "react";
import { LoadingComponent, useExecutionDataView } from "@gooddata/sdk-ui";
import { CustIDByScope, useBrandConfigurator } from "../../contexts/BrandConfiguratorProvider";
import MeasuresContext from "../../contexts/Measures";
import HighchartsComponent from "../HighchartsComponent";
import * as Md from "../../md/full";
import FiltersContext from "../../contexts/Filters";
import { calculationModes, transformSlicesToBrandTree } from "../../routes/BrandModelAnalysis";
import { calculateAvg, mode } from "../../utils/calculationUtils";
import _ from "lodash";
import { useAppContext } from "../../contexts/AppContext";

const widgetId = "brand-model-column-chart";

const ColumnModelAnalysis = ({ filters, calculationHandler, selectedPlayers, showListPrices = true, ...props }) => {
    const { filterSelectors, deselectConfigurator } = useBrandConfigurator();
    const { selectedKpi } = useContext(MeasuresContext);
    const { getFirstFilterValue } = useContext(FiltersContext);
    // Group by make
    const [series, setSeries] = useState([]);
    const [categories, setCategories] = useState([]);

    const countryId = getFirstFilterValue("filterCountryId");
    const channel1 = getFirstFilterValue("filterChannel1");

    useEffect(() => {
        if (selectedPlayers.length > 0) {
            const preparedData = Object.entries(filterSelectors).reduce((acc, [make, modelsObject]) => {
                const flatModelsArr = Object.entries(modelsObject).reduce((accModel, [model, versionArr]) => {
                    const listPricesByVersion = versionArr.reduce((accLpVersion, versionObj) => {
                        const listPricesByCustIds = selectedPlayers.filter((p) => p.custId in versionObj && Number(versionObj[p.custId][1]) > 0).map((p) => versionObj[p.custId][1]);
                        accLpVersion[versionObj.label] = [...listPricesByCustIds];
                        return accLpVersion;
                    }, {});
                    const modeLpByVersion = Object.entries(listPricesByVersion).reduce((accLp, [key, arrLp]) => {
                        accLp[key] = Number(mode(arrLp)) > 0 ? Math.round(Number(mode(arrLp))) : null;
                        return accLp;
                    }, {});

                    selectedPlayers.forEach((p) => {
                        const versionValuesByCustId = versionArr.filter((v) => v[p.custId] && v[p.custId][selectedKpi.index] > 0).map((v) => v[p.custId][selectedKpi.index]);
                        const custIdVal = versionValuesByCustId.length > 0 ? selectedKpi.roundValue(calculationHandler(versionValuesByCustId)) : null;
                        const index = accModel.findIndex((o) => o.make === make && o.model === model);
                        if (index !== -1) {
                            accModel[index][p.custId] = custIdVal;
                        } else {
                            accModel.push({
                                make: make,
                                model: model,
                                [p.custId]: custIdVal,
                                listPrice: Math.round(calculateAvg(Object.values(modeLpByVersion).filter((val) => Number(val) > 0))),
                            });
                        }
                    });
                    return accModel;
                }, []);

                // Find the min for each model
                const calculatedData = flatModelsArr.map((modelObj) => ({
                    ...modelObj,
                    y: _.min(
                        Object.entries(modelObj)
                            .filter(([key, _]) => selectedPlayers.map((p) => p.custId).includes(key))
                            .map(([_, val]) => val),
                    ),
                }));
                acc = [...acc, ...calculatedData];
                return acc;
            }, []);

            // Average by make used to sort the bars
            const makesObj = preparedData.reduce((accMakeAvg, item) => {
                accMakeAvg[item.make] = item.make in accMakeAvg ? [...accMakeAvg[item.make], item.y] : [item.y];
                return accMakeAvg;
            }, {});

            const makeAvgObj = Object.entries(makesObj).reduce((acc, [key, arr]) => {
                acc[key] = calculateAvg(arr);
                return acc;
            }, {});

            preparedData.sort((a, b) => b.y - a.y).sort((a, b) => (a.make in makeAvgObj && b.make in makeAvgObj ? makeAvgObj[b.make] - makeAvgObj[a.make] : 0));

            const categoriesArr = preparedData.reduce((acc, obj) => {
                const index = acc.findIndex((o) => o.name === obj.make);
                if (index !== -1) {
                    acc[index].categories.push(obj.model);
                } else {
                    acc.push({
                        name: obj.make,
                        categories: [obj.model],
                    });
                }
                return acc;
            }, []);

            setCategories([...categoriesArr]);
            setSeries([
                {
                    name: "Cheapest",
                    color: "#7189AA",
                    maxPointWidth: categoriesArr?.map((obj) => obj.categories).flat().length > 15 ? 35 : 45,
                    data: preparedData.flat(),
                    point: {
                        events: {
                            click: function (event) {
                                deselectConfigurator(this.options.make, this.options.model);
                            },
                        },
                    },
                    // showInLegend: false,
                },
                ...selectedPlayers.map((p) => ({
                    name: p.custLabel,
                    type: "spline",
                    pointPlacement: showListPrices ? pointPlacement[categoriesArr?.map((obj) => obj.categories).flat().length] || -0.23 : 0,
                    color: p.custColor,
                    data: preparedData.map((obj) => (p.custId in obj ? obj[p.custId] : null)),
                    showInLegend: preparedData.find((obj) => p.custId in obj && obj[p.custId]) !== undefined,
                    marker: {
                        symbol: "circle",
                        fillColor: p.custColor,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                    },
                    states: {
                        hover: {
                            enabled: false,
                        },
                    },
                })),
                {
                    name: "List price",
                    yAxis: 1,
                    allowPointSelect: false,
                    maxPointWidth: categoriesArr?.map((obj) => obj.categories).flat().length > 15 ? 17 : 25,
                    color: "#FF7D93",
                    data: preparedData.flat().map((dt) => ({
                        ...dt,
                        y: dt.listPrice,
                    })),
                    events: {
                        legendItemClick: function () {
                            return false;
                        },
                    },
                    enableMouseTracking: false,
                    showInLegend: showListPrices,
                    visible: showListPrices,
                },
            ]);
        }
    }, [filterSelectors, selectedKpi, calculationHandler, selectedPlayers, showListPrices]);

    return series?.length > 0 && categories.length > 0 ? (
        <>
            <ColumnChartWithNestedLabels categories={categories} series={series} filters={filters} menuButtonPosition={"inside"} showListPrices={showListPrices} {...props} />
            {props.footnote && <span className="pl-3 pb-2 text-left text-xs font-medium">{props.footnote}</span>}
        </>
    ) : (
        <LoadingComponent />
    );
};

const ColumnModelAnalysisWidget = ({ filters, calculationMode = "average", ...props }) => {
    const { selectedKpi, seriesBy } = useContext(MeasuresContext);
    const { getFirstFilterValue } = useContext(FiltersContext);
    const { getCustlabelByCustId, getColorByCustId } = useAppContext();

    const { result, status } = useExecutionDataView({
        execution: {
            seriesBy,
            slicesBy: [Md.CorrMake, Md.CorrModel, Md.CustId, Md.VersionStd],
            filters,
        },
    });

    // Group by make
    const [series, setSeries] = useState([]);
    const [categories, setCategories] = useState([]);

    const countryId = getFirstFilterValue("filterCountryId");
    const channel1 = getFirstFilterValue("filterChannel1");
    const playerFilter = filters.find((obj) => obj.positiveAttributeFilter.displayForm.identifier === Md.CustId.attribute.displayForm.identifier);
    const playerCustIDs = playerFilter?.positiveAttributeFilter.in.values.length > 0 ? playerFilter.positiveAttributeFilter.in.values : ["OEM", "CAP"];
    const selectedPlayers = playerCustIDs.map((custId) => ({
        custId: custId,
        custLabel: getCustlabelByCustId(custId),
        custColor: getColorByCustId(custId),
    }));

    useEffect(() => {
        const slices = result?.data().slices().toArray();
        const dataByBrand = transformSlicesToBrandTree(slices);
        if (dataByBrand) {
            const preparedData = Object.entries(dataByBrand).reduce((acc, [make, modelsObject]) => {
                const flatModelsArr = Object.entries(modelsObject).reduce((accModel, [model, versionArr]) => {
                    const listPricesByVersion = versionArr.reduce((accLpVersion, versionObj) => {
                        const listPricesByCustIds = selectedPlayers.filter((p) => p.custId in versionObj && Number(versionObj[p.custId][1]) > 0).map((p) => versionObj[p.custId][1]);
                        accLpVersion[versionObj.label] = [...listPricesByCustIds];
                        return accLpVersion;
                    }, {});
                    const modeLpByVersion = Object.entries(listPricesByVersion).reduce((accLp, [key, arrLp]) => {
                        accLp[key] = Number(mode(arrLp)) > 0 ? Math.round(Number(mode(arrLp))) : null;
                        return accLp;
                    }, {});

                    selectedPlayers.forEach((p) => {
                        const versionValuesByCustId = versionArr.filter((v) => v[p.custId] && v[p.custId][selectedKpi.index] > 0).map((v) => v[p.custId][selectedKpi.index]);
                        const custIdVal = versionValuesByCustId.length > 0 ? selectedKpi.roundValue(calculationModes[calculationMode].calculationMethod(versionValuesByCustId)) : null;
                        const index = accModel.findIndex((o) => o.make === make && o.model === model);
                        if (index !== -1) {
                            accModel[index][p.custId] = custIdVal;
                        } else {
                            accModel.push({
                                make: make,
                                model: model,
                                [p.custId]: custIdVal,
                                listPrice: Math.round(calculateAvg(Object.values(modeLpByVersion).filter((val) => Number(val) > 0))),
                            });
                        }
                    });
                    return accModel;
                }, []);

                // Find the min for each model
                const calculatedData = flatModelsArr.map((modelObj) => ({
                    ...modelObj,
                    y: _.min(
                        Object.entries(modelObj)
                            .filter(([key, _]) => selectedPlayers.map((p) => p.custId).includes(key))
                            .map(([_, val]) => val),
                    ),
                }));
                acc = [...acc, ...calculatedData];
                return acc;
            }, []);

            // Average by make used to sort the bars
            const makesObj = preparedData.reduce((accMakeAvg, item) => {
                accMakeAvg[item.make] = item.make in accMakeAvg ? [...accMakeAvg[item.make], item.y] : [item.y];
                return accMakeAvg;
            }, {});

            const makeAvgObj = Object.entries(makesObj).reduce((acc, [key, arr]) => {
                acc[key] = calculateAvg(arr);
                return acc;
            }, {});

            preparedData.sort((a, b) => a.y - b.y).sort((a, b) => (a.make in makeAvgObj && b.make in makeAvgObj ? makeAvgObj[b.make] - makeAvgObj[a.make] : 0));

            const categoriesArr = preparedData.reduce((acc, obj) => {
                const index = acc.findIndex((o) => o.name === obj.make);
                if (index !== -1) {
                    acc[index].categories.push(obj.model);
                } else {
                    acc.push({
                        name: obj.make,
                        categories: [obj.model],
                    });
                }
                return acc;
            }, []);

            setCategories([...categoriesArr]);
            setSeries([
                {
                    name: "Cheapest",
                    color: "#7189AA",
                    data: preparedData.flat(),
                    enableMouseTracking: false,
                    // showInLegend: false,
                },
                ...selectedPlayers.map((p) => ({
                    name: p.custLabel,
                    type: "spline",
                    color: p.custColor,
                    pointPlacement: props.showListPrices ? pointPlacement[categoriesArr?.map((obj) => obj.categories).flat().length] || -0.23 : 0,
                    data: preparedData.map((obj) => (p.custId in obj ? obj[p.custId] : null)),
                    showInLegend: preparedData.find((obj) => p.custId in obj && obj[p.custId]) !== undefined,
                    marker: {
                        symbol: "circle",
                        fillColor: p.custColor,
                    },
                })),
                {
                    name: "List price",
                    yAxis: 1,
                    maxPointWidth: 25,
                    color: "#FF7D93",
                    data: preparedData.flat().map((dt) => ({
                        ...dt,
                        y: dt.listPrice,
                    })),
                    events: {
                        legendItemClick: function () {
                            return false;
                        },
                    },
                    enableMouseTracking: false,
                    showInLegend: props.showListPrices,
                    visible: props.showListPrices,
                },
            ]);
        }
    }, [result, calculationMode]);

    return status !== "loading" && series?.length > 0 && categories.length > 0 ? (
        <ColumnChartWithNestedLabels categories={categories} series={series} selectedPlayers={selectedPlayers} {...props} />
    ) : (
        <div className="flex flex-col items-center justify-center gap-2 min-h-[400px]">
            <div>
                <LoadingComponent />
            </div>
            <span>Loading...</span>
        </div>
    );
};

const ColumnChartWithNestedLabels = ({ categories, series, ...props }) => {
    const barCount = categories?.map((o) => o.categories).flat().length;

    return (
        <HighchartsComponent
            widgetProps={{
                id: widgetId,
                filters: props.filters,
                section: "oem-views",
                calculationMode: props.calculationMode,
                showListPrices: props.showListPrices,
            }}
            options={{
                chart: {
                    type: "column",
                    // width: width,
                    height: props.height || 500,
                },
                plotOptions: {
                    series: {
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                    },
                    column: {
                        grouping: true,
                        // color: "#3a0ca3",
                        // colorByPoint: true,
                        borderWidth: 0,
                        borderRadius: 2,
                        pointPadding: 0.05,
                        // groupPadding: categories?.map((o) => o.categories).flat().length > 3 ? 0.35 : 0.45 - categories?.map((o) => o.categories).flat().length / 100,
                        // Reduce n% of space between groups, depending on the number of categories
                        groupPadding: 0.48 - (barCount > 1 ? 2.5 * (barCount / 100) : 0),
                        dataLabels: {
                            enabled: true,
                        },
                    },
                    spline: {
                        lineWidth: 0,
                        states: {
                            hover: {
                                enabled: false,
                            },
                        },
                        stickyTracking: false,
                        marker: {
                            radius: 5,
                            fillColor: "#ff7d93",
                        },

                        cursor: "pointer",
                    },
                },
                title: {
                    text: props.title,
                    align: "center",
                    style: {
                        fontSize: "16px",
                        fontWeight: 600,
                        color: "#3a0ca3",
                    },
                },
                subtitle: {
                    text: props.subtitle,
                    align: "center",
                    style: {
                        fontSize: "12px",
                        fontWeight: 600,
                        color: "#506e96",
                    },
                },
                legend: {
                    padding: 10,
                    margin: 10,
                    align: "center",
                    symbolRadius: 1,
                },
                tooltip: {
                    formatter: function () {
                        return `${this.x.parent.name}, ${this.x.name}<br/>${this.series.name} <b>${this.y}</b>`;
                    },
                },
                xAxis: {
                    categories: categories,
                    labels: {
                        // align: 'center',
                        groupedOptions: [
                            {
                                rotation: 0,
                                style: {
                                    paddingTop: "10px",
                                    margin: "100px",
                                },
                            },
                            {
                                rotation: 0,
                                style: {
                                    paddingTop: "10px",
                                    fontSize: "4px",
                                },
                            },
                        ],
                        rotation: 0,
                        style: {
                            paddingTop: "10px",
                            paddingBottom: "0px",
                            fontFamily: "Verdana, sans-serif",
                        },
                    },
                    title: {
                        text: null,
                    },
                    // min: -0.75,
                    // max: Object.keys(filterSelectors).length-1 + 0.75
                },
                yAxis: [
                    {
                        // labels: {
                        //     enabled: false,
                        // },
                        title: {
                            text: null,
                        },
                        min: 0,
                        // max: series[0].y * 1.1,
                        // tickAmount: null,
                    },
                    {
                        title: {
                            text: null,
                        },
                        labels: {
                            enabled: false,
                        },
                        type: "logarithmic",
                        opposite: true,
                        tickInterval: 10,
                    },
                ],
                series: [...series],
                lang: {
                    noData: "No data available",
                },
                noData: {
                    style: {
                        fontWeight: "bold",
                        fontSize: "15px",
                        color: "#595959",
                    },
                },
            }}
            {...props}
        />
    );
};

// Static placement of the points on top of kpi column
const pointPlacement = {
    1: -0.01,
    2: -0.035,
    3: -0.045,
    4: -0.055,
    5: -0.07,
    6: -0.085,
    7: -0.1,
    8: -0.1,
    9: -0.12,
    10: -0.13,
    11: -0.15,
    12: -0.16,
    13: -0.18,
    14: -0.19,
    15: -0.2,
    16: -0.2,
    17: -0.2,
};

export { ColumnModelAnalysis, ColumnModelAnalysisWidget };
